import { useFetchPersons } from "hooks/usePersonsApi";
import { uniqWith } from "lodash-es";
import { useEffect, useState } from "react";
import { createPagination } from "utils/createPagination";

const DefaultPageSize = 10;

type PersonsState = {
  offsetDb: number;
  offsetDc: number;
  searchText: string;
  currentPage: number;
  moreDataExists: boolean;
  persons: Array<any>;
};

type Props = { entityId: string; enabled: boolean };

const createPersonState = (searchText: string) => ({
  offsetDb: 0,
  offsetDc: 0,
  searchText,
  currentPage: 1,
  moreDataExists: false,
  persons: [],
});

export const usePagedPersons = ({ entityId, enabled }: Props) => {
  const [currentState, setCurrentState] = useState<PersonsState>(createPersonState(""));

  const { data: personsResponse, isLoading } = useFetchPersons({
    entityId,
    searchText: currentState.searchText,
    offsetDb: currentState.offsetDb,
    offsetDc: currentState.offsetDc,
    limit: DefaultPageSize + DefaultPageSize,
    enabled,
  });

  const pagination = createPagination(currentState.persons.length, DefaultPageSize, currentState.currentPage);

  const onTableChangedHandler = (p: any) => {
    const isLastPage = p.current === p.total / p.pageSize;

    setCurrentState((prev) => ({
      ...prev,
      currentPage: p.current,
      ...(isLastPage &&
        prev.moreDataExists && {
          offsetDc: prev.persons.filter((x) => !!x.dcPersonId).length,
          offsetDb: prev.persons.filter((x) => !!x.personId).length,
        }),
    }));
  };

  const onSearchTextUpdatedHandler = (searchText: string) =>
    setCurrentState((prev) => (prev.searchText === searchText ? prev : createPersonState(searchText)));

  useEffect(() => {
    setCurrentState((prev) => ({
      ...prev,
      moreDataExists: personsResponse?.data?.length === DefaultPageSize + DefaultPageSize,
      persons: uniqWith(
        prev.persons.concat(personsResponse?.data || []),
        (a: any, b: any) => a?.dcPersonId === b?.dcPersonId && a?.personId === b?.personId
      ),
    }));
  }, [personsResponse]);

  return {
    persons: currentState.persons,
    isLoading,
    pagination,
    onTableChangedHandler,
    onSearchTextUpdatedHandler,
  };
};
